import { useEffect, useRef } from "react"
import { useFormContext } from "@carrotfertility/carotene-core-x"
import type { UseFormSetValue } from "react-hook-form"
import { TOTAL_STEP_SLOTS, arrangeStepRows, slotRecommendedSteps } from "../utils/stepHelpers"
import type { CarrotPlanFormOption, MenuItemOptionFields } from "./useCarrotPlanMappings"
import { FieldName } from "../CarrotPlanFormTypes"
import type { CarrotPlanFormStep, UnslottedStep } from "../CarrotPlanFormTypes"
import { isMandatory } from "../carrotPlanAutocompleteHelpers"

type UseResetFormStepsOnJourneyChangeArgs = {
  journey: number
  stage: number
  newRequiredItems: CarrotPlanFormOption<MenuItemOptionFields>[] | null
  newPrioritizedItems: CarrotPlanFormOption<MenuItemOptionFields>[] | null
  enableRecommendations?: boolean
  newRecommendedSteps?: UnslottedStep[] | null
}

function applyStepToForm(setValue: UseFormSetValue<any>, step: CarrotPlanFormStep, index: number): void {
  const opts = { shouldDirty: true }
  setValue(`${FieldName.steps}.${index}.${FieldName.menuItem}`, step.menuItem ?? null, opts)
  setValue(`${FieldName.steps}.${index}.${FieldName.topic}`, step.topic ?? null, opts)
  setValue(`${FieldName.steps}.${index}.${FieldName.content}`, step.content ?? null, opts)
  setValue(`${FieldName.steps}.${index}.${FieldName.url}`, step.url ?? null, opts)
}

/**
 * Builds new mandatory (required or prioritized) step rows, carrying over media
 * (topic / content / url) from any prior step sharing the same menuItem.value.
 * Returns unslotted steps — `arrangeStepRows` stamps `id` + `stepIndex` downstream.
 */
export function rebuildMandatorySteps(
  newMandatoryItems: CarrotPlanFormOption<MenuItemOptionFields>[],
  previousSteps: CarrotPlanFormStep[]
): UnslottedStep[] {
  const previousByMenuValue = new Map<string, CarrotPlanFormStep>()
  for (const step of previousSteps) {
    const id = step.menuItem?.value
    if (id != null) previousByMenuValue.set(id, step)
  }

  return newMandatoryItems.slice(0, TOTAL_STEP_SLOTS).map((item) => {
    const carryover = previousByMenuValue.get(item.value)
    return {
      menuItem: item,
      topic: carryover?.topic,
      content: carryover?.content,
      url: carryover?.url ?? item.optionFields?.defaultUrl ?? null
    }
  })
}

/**
 * Returns previous user-added step rows that survive a swap: non-empty,
 * non-required, non-prioritized, and not colliding with `newMandatoryIds`.
 */
export function getUserAddedSteps(
  previousSteps: CarrotPlanFormStep[],
  newMandatoryIds: Set<string>
): CarrotPlanFormStep[] {
  return previousSteps.filter((step) => {
    if (step.menuItem == null) return false
    if (isMandatory(step.menuItem.optionMetadata)) return false
    if (newMandatoryIds.has(step.menuItem.value)) return false
    return true
  })
}

const selectionKey = (journey: number, stage: number): string => `${journey}:${stage}`

/**
 * Re-seeds the form's step rows when the primary journey or stage changes (gate-off path)
 * or when fresh recommendations arrive (gate-on path), preserving user-added steps and
 * (where possible) media selections.
 *
 * Two branches that are mutually exclusive in a single run:
 *   1. Journey just changed → drop the prior selection's state now. The early
 *      `return` is load-bearing: react-query may have cached the
 *      (newJourney, staleStage) options, which would otherwise let branch 2
 *      fire with the wrong stage's data. Gate-on uses `hardClearAllSteps` so
 *      rec-loaded rows don't bleed across selections; gate-off preserves
 *      user-added rows via `swapItems([], [])`.
 *   2. Caller signals data ready for the new (journey, stage) → full swap.
 *      Gate-on consumes `newRecommendedSteps` via `slotRecommendedSteps`
 *      (engine owns slot order). Gate-off consumes `newRequiredItems` +
 *      `newPrioritizedItems` via `swapItems`. The `gateChanged` check covers
 *      async LD hydration where the gate flips for the same selection.
 */
export function useResetFormStepsOnJourneyChange({
  journey,
  stage,
  newRequiredItems,
  newPrioritizedItems,
  enableRecommendations = false,
  newRecommendedSteps = null
}: UseResetFormStepsOnJourneyChangeArgs): void {
  const { setValue, getValues } = useFormContext()

  const lastJourneyRef = useRef(journey)
  const wasGateOnRef = useRef(enableRecommendations)
  // Start as null so the effect treats the first non-null (journey, stage) as a
  // change and runs the initial-fill branch. The parent renders with empty
  // defaultValues, then hydrates them — so "first real value" has to look like
  // a transition, not the initial state.
  const lastSwappedSelectionRef = useRef<string | null>(null)

  useEffect(() => {
    const swapItems = (
      requiredItems: CarrotPlanFormOption<MenuItemOptionFields>[],
      prioritizedItems: CarrotPlanFormOption<MenuItemOptionFields>[]
    ) => {
      const previousSteps = (getValues(FieldName.steps) ?? []) as CarrotPlanFormStep[]
      const newMandatoryIds = new Set([...requiredItems.map((m) => m.value), ...prioritizedItems.map((m) => m.value)])

      const requiredSteps = rebuildMandatorySteps(requiredItems, previousSteps)
      const prioritizedSteps = rebuildMandatorySteps(prioritizedItems, previousSteps)
      const userAddedSteps = getUserAddedSteps(previousSteps, newMandatoryIds)

      const nextSteps = arrangeStepRows(userAddedSteps, requiredSteps, prioritizedSteps)
      nextSteps.forEach((step, index) => applyStepToForm(setValue, step, index))
    }

    // Full replacement: drop any prior steps (including user-added) and lay out the
    // new recs in the order the backend returned them. Used when the gate is on;
    // the recommendations engine owns slot order, so the frontend just pads + stamps ids.
    const swapRecommendations = (recommendedSteps: UnslottedStep[]) => {
      const nextSteps = slotRecommendedSteps(recommendedSteps)
      nextSteps.forEach((step, index) => applyStepToForm(setValue, step, index))
    }

    // Wipes every slot to empty. Used when the gate-on path is involved in a journey
    // change — rec-filled rows aren't user-added and may not be flagged isMandatory,
    // so `getUserAddedSteps` would otherwise preserve them. Empty in all three
    // categories produces TOTAL_STEP_SLOTS empty rows.
    const hardClearAllSteps = () => {
      arrangeStepRows([], [], []).forEach((step, index) => applyStepToForm(setValue, step, index))
    }

    // Branch 1: journey just changed. Drop the prior selection's state now; we don't
    // yet know what the new (journey, stage) needs.
    if (lastJourneyRef.current !== journey) {
      if (wasGateOnRef.current || enableRecommendations) {
        // Gate-on involved on either side of the change — rec-loaded rows are not
        // user-added (and may not be flagged isMandatory); clear everything so they
        // don't bleed into the new selection.
        hardClearAllSteps()
      } else {
        // Pure legacy path: preserve user-added rows as before.
        swapItems([], [])
      }
      lastJourneyRef.current = journey
      lastSwappedSelectionRef.current = null
      wasGateOnRef.current = enableRecommendations
      return
    }

    // Branch 2: caller has handed us the data for this selection and we haven't
    // already swapped for it. The data shape depends on the gate. The gate-changed
    // check covers async LD hydration: enableRecommendations can flip after the
    // initial swap for the same (journey, stage) once the flag value arrives.
    const currentSelection = selectionKey(journey, stage)
    const hasData = enableRecommendations
      ? newRecommendedSteps != null
      : newRequiredItems != null || newPrioritizedItems != null
    const selectionChanged = lastSwappedSelectionRef.current !== currentSelection
    const gateChanged = wasGateOnRef.current !== enableRecommendations
    const shouldSwap = hasData && (selectionChanged || gateChanged)
    if (!shouldSwap) return

    if (enableRecommendations) {
      if (newRecommendedSteps == null) return
      swapRecommendations(newRecommendedSteps)
    } else {
      swapItems(newRequiredItems ?? [], newPrioritizedItems ?? [])
    }
    lastSwappedSelectionRef.current = currentSelection
    wasGateOnRef.current = enableRecommendations
  }, [
    newRequiredItems,
    newPrioritizedItems,
    newRecommendedSteps,
    enableRecommendations,
    journey,
    stage,
    setValue,
    getValues
  ])
}
